import React, { useState, useEffect } from 'react';
import { motion, useAnimation, useInView } from 'framer-motion';
import { useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { parseFile } from '../utils/pdfParser';
import { saveResume, updateUserStats, checkAndAwardBadges, getUserStats } from '../services/firebaseService';
import aiService from '../services/aiService';
import { ProfessionalTemplate, CreativeTemplate, MinimalistTemplate, TemplateSelector } from './ResumeTemplates';
import AuthModal from './AuthModal';
import DashboardReal from './DashboardReal';
import CareerHub from './CareerHub';
import EnhancedFreemiumResumeApp from './EnhancedFreemiumResumeApp';
import AutoRotatingTestimonials from './AutoRotatingTestimonials';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import RefundPolicy from './pages/RefundPolicy';
import ShippingPolicy from './pages/ShippingPolicy';
import ContactUs from './pages/ContactUs';
import { 
  Upload, 
  ClipboardList, 
  Sparkles, 
  Target, 
  Shield, 
  TrendingUp, 
  Check,
  ArrowRight,
  Star,
  ChevronDown,
  Menu,
  X,
  FileText,
  Download,
  Brain,
  Zap,
  Copy,
  Eye,
  CheckCircle,
  AlertCircle,
  User,
  LogIn,
  Layout,
  Palette,
  RefreshCw,
  Award,
  Users,
  Briefcase
} from 'lucide-react';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1
    }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: "easeOut" } }
};

// Navigation Header with Shortlistly Branding
const Header = ({ onNavigate, currentView }) => {
  const { user } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [showAuthModal, setShowAuthModal] = useState(false);

  const menuItems = [
    { id: 'home', label: 'Home', href: '#home' },
    { id: 'app', label: 'AI Resume Optimizer', href: '#app' },
    // Hidden career hub - keeping code intact
    // { id: 'career-hub', label: 'Career Hub', href: '/career-hub', external: true },
    { id: 'testimonials', label: 'Testimonials', href: '#testimonials' },
    { id: 'pricing', label: 'Pricing', href: '#pricing' }
  ];

  const scrollToSection = (sectionId) => {
    // Handle external navigation (currently hidden)
    if (sectionId === 'career-hub') {
      if (user) {
        window.location.href = '/career-hub';
      } else {
        setShowAuthModal(true);
      }
      return;
    }

    if (currentView !== 'landing') {
      onNavigate('landing');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        setActiveSection(sectionId);
        setIsMenuOpen(false);
      }
    }
  };

  const handleTryApp = () => {
    // Direct scroll to app section - no authentication required
    scrollToSection('app');
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-lg border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => onNavigate('landing')}>
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Award className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Shortlistly
              </span>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {menuItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`px-3 py-2 text-sm font-medium transition-colors ${
                    activeSection === item.id
                      ? 'text-blue-600 bg-blue-50 rounded-lg'
                      : 'text-slate-700 hover:text-blue-600'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            {/* User Actions */}
            <div className="hidden md:flex items-center space-x-4">
              {/* Hidden sign-in options - keeping code intact */}
              {false && user ? (
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => onNavigate('dashboard')}
                    className="flex items-center px-4 py-2 text-slate-700 hover:text-blue-600 transition-colors"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Dashboard
                  </button>
                  <img 
                    src={user.photoURL} 
                    alt="Profile" 
                    className="w-8 h-8 rounded-full cursor-pointer"
                    onClick={() => onNavigate('dashboard')}
                  />
                </div>
              ) : false && (
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="flex items-center px-4 py-2 text-slate-700 hover:text-blue-600 transition-colors"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign In
                </button>
              )}
              
              <button
                onClick={handleTryApp}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300"
              >
                Get Shortlisted Free
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-slate-700"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-slate-200 bg-white"
            >
              <div className="py-4 space-y-2">
                {menuItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="block w-full text-left px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-lg"
                  >
                    {item.label}
                  </button>
                ))}
                {/* Hidden auth options in mobile menu - keeping code intact */}
                {false && user ? (
                  <button
                    onClick={() => onNavigate('dashboard')}
                    className="block w-full text-left px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-lg"
                  >
                    Dashboard
                  </button>
                ) : false && (
                  <button
                    onClick={() => setShowAuthModal(true)}
                    className="block w-full text-left px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-lg"
                  >
                    Sign In
                  </button>
                )}
                <button
                  onClick={handleTryApp}
                  className="block w-full text-left px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg mt-4"
                >
                  Get Shortlisted Free
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </header>

      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => {
          setShowAuthModal(false);
          // If they were trying to access Career Hub, redirect there
          const currentTarget = window.location.hash;
          if (currentTarget === '#career-hub') {
            window.location.href = '/career-hub';
          } else {
            scrollToSection('app');
          }
        }}
      />
    </>
  );
};

// Animated Section Component
const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={fadeInUp}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Hero Section - Updated with Shortlistly Messaging
const HeroSection = () => {
  return (
    <section id="home" className="relative min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 overflow-hidden pt-16">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%234F46E5' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
      </div>
      
      {/* Glass morphism overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-indigo-500/10 backdrop-blur-3xl"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-screen">
          {/* Left Content */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center lg:text-left"
          >
            <motion.div variants={fadeInUp} className="mb-6">
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 text-blue-300 text-sm font-medium">
                <Award className="w-4 h-4 mr-2" />
                AI-Powered Career Optimization
              </span>
            </motion.div>
            
            <motion.h1 
              variants={fadeInUp}
              className="text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight"
            >
              Get{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">
                Shortlisted
              </span>
              .{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400">
                Get Hired
              </span>
              .
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-xl text-blue-100 mb-8 max-w-2xl leading-relaxed"
            >
              Your resume isn't just a file — it's your first impression.
              Shortlistly uses AI to rewrite, reformat, and optimize your resume to beat ATS bots and impress real recruiters.
            </motion.p>

            <motion.div 
              variants={fadeInUp}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 mb-8"
            >
              <div className="space-y-3 text-white">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                  <span>Cut 6+ page resumes down to a sharp 2-pager</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                  <span>Add the right keywords recruiters search for</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                  <span>Get noticed. Land interviews faster.</span>
                </div>
              </div>
            </motion.div>
            
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-6">
              <motion.button
                onClick={() => {
                  const appSection = document.getElementById('app');
                  if (appSection) appSection.scrollIntoView({ behavior: 'smooth' });
                }}
                whileHover={{ scale: 1.05, boxShadow: "0 25px 50px -12px rgba(59, 130, 246, 0.5)" }}
                whileTap={{ scale: 0.95 }}
                className="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-2xl shadow-2xl hover:shadow-blue-500/25 transition-all duration-300"
              >
                <span className="relative z-10 flex items-center justify-center">
                  Get Shortlisted Free
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl blur opacity-0 group-hover:opacity-75 transition-opacity"></div>
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  const pricingSection = document.getElementById('pricing');
                  if (pricingSection) pricingSection.scrollIntoView({ behavior: 'smooth' });
                }}
                className="px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-2xl backdrop-blur-sm hover:bg-white/10 transition-all duration-300"
              >
                View Pricing
              </motion.button>
            </motion.div>
            
            <motion.div 
              variants={fadeInUp}
              className="text-center lg:text-left mb-8"
            >
              <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                <span className="text-blue-200 text-sm">
                  <strong className="text-white">Free:</strong> Resume analysis • 
                  <strong className="text-white"> Basic ₹199:</strong> 3 optimizations • 
                  <strong className="text-white"> Premium ₹499:</strong> 9 templates + Unlimited access
                </span>
              </div>
            </motion.div>
            
            <motion.div 
              variants={fadeInUp}
              className="mt-12 grid grid-cols-3 gap-8 text-center lg:text-left"
            >
              <div>
                <div className="text-3xl font-bold text-white mb-1">95%</div>
                <div className="text-blue-300 text-sm">Get Shortlisted</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-white mb-1">50K+</div>
                <div className="text-blue-300 text-sm">Success Stories</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-white mb-1">3x</div>
                <div className="text-blue-300 text-sm">Faster Hiring</div>
              </div>
            </motion.div>
          </motion.div>
          
          {/* Right Content - Hero Image */}
          <motion.div
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="relative"
          >
            <div className="relative">
              {/* Floating cards */}
              <motion.div
                animate={{ y: [-10, 10, -10] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="absolute -top-8 -left-8 bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20"
              >
                <div className="flex items-center space-x-2">
                  <Target className="w-5 h-5 text-green-400" />
                  <span className="text-white text-sm">ATS Optimized</span>
                </div>
              </motion.div>
              
              <motion.div
                animate={{ y: [10, -10, 10] }}
                transition={{ duration: 3, repeat: Infinity, delay: 1 }}
                className="absolute -bottom-8 -right-8 bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20"
              >
                <div className="flex items-center space-x-2">
                  <Award className="w-5 h-5 text-blue-400" />
                  <span className="text-white text-sm">Shortlisted</span>
                </div>
              </motion.div>
              
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.pexels.com/photos/7997398/pexels-photo-7997398.jpeg" 
                  alt="Career Success - Get Shortlisted" 
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 to-transparent"></div>
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <ChevronDown className="w-6 h-6 text-blue-300" />
        </motion.div>
      </div>
    </section>
  );
};

// How It Works Section - Updated for Shortlistly
const HowItWorksSection = () => {
  const steps = [
    {
      icon: Upload,
      title: "Upload Your Resume",
      description: "Upload your existing resume or copy & paste the content. Our AI analyzes your experience and identifies improvement opportunities.",
      image: "https://images.unsplash.com/photo-1618544976420-1f213fcf2052"
    },
    {
      icon: Briefcase,
      title: "Add Job Details",
      description: "Paste the job description from your target role. Shortlistly identifies key requirements and optimizes your resume accordingly.",
      image: "https://images.pexels.com/photos/3889053/pexels-photo-3889053.jpeg"
    },
    {
      icon: Award,
      title: "Get Shortlisted",
      description: "Receive an optimized resume that beats ATS systems and impresses recruiters. Download in multiple formats instantly.",
      image: "https://images.unsplash.com/photo-1544717297-fa95b6ee9643"
    }
  ];

  return (
    <section id="how-it-works" className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection className="text-center mb-16">
          <motion.h2 
            variants={fadeInUp}
            className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6"
          >
            How Shortlistly Works
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="text-xl text-slate-600 max-w-3xl mx-auto"
          >
            Transform your career in just 3 simple steps with our AI-powered optimization platform
          </motion.p>
        </AnimatedSection>
        
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid md:grid-cols-3 gap-8"
        >
          {steps.map((step, index) => (
            <motion.div
              key={index}
              variants={scaleIn}
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
              className="relative group"
            >
              <div className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border-2 border-slate-100 hover:border-blue-200">
                {/* Step number */}
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-lg">
                  {index + 1}
                </div>
                
                {/* Icon */}
                <div className="w-16 h-16 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <step.icon className="w-8 h-8 text-blue-600" />
                </div>
                
                {/* Content */}
                <h3 className="text-2xl font-bold text-slate-900 mb-4">{step.title}</h3>
                <p className="text-slate-600 mb-6 leading-relaxed">{step.description}</p>
                
                {/* Image */}
                <div className="rounded-2xl overflow-hidden">
                  <img 
                    src={step.image} 
                    alt={step.title}
                    className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
              
              {/* Connector line */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-blue-400 to-indigo-400"></div>
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Highlighted Quote Section - Our Philosophy
const HighlightedQuoteSection = () => {
  return (
    <section className="py-20 bg-gradient-to-r from-indigo-600 to-purple-600">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl p-12 border border-white/20"
        >
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-8">
            <Sparkles className="w-10 h-10 text-white" />
          </div>
          
          <motion.h2 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-3xl lg:text-4xl font-bold text-white mb-8 leading-tight"
          >
            Our Philosophy
          </motion.h2>
          
          <motion.blockquote 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="text-xl lg:text-2xl text-white/90 font-medium leading-relaxed italic"
          >
            "Not to fake anything. Not to change your story. Just to shine the spotlight on the right chapters."
          </motion.blockquote>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-8 h-1 w-24 bg-white/40 mx-auto rounded-full"
          ></motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Enhanced Testimonials Section with Auto-Rotation
const EnhancedTestimonialsSection = () => {
  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Software Engineer",
      company: "Tech Corp",
      content: "Shortlistly helped me land my dream job at a top tech company. The AI optimization made my resume stand out and get shortlisted from hundreds of applications!",
      rating: 5,
      avatar: "https://images.pexels.com/photos/5921483/pexels-photo-5921483.jpeg"
    },
    {
      name: "Michael Chen",
      role: "Marketing Manager",
      company: "Growth Co",
      content: "I was struggling to get shortlisted until I used Shortlistly. Within a week, I had 5 interview requests. The ATS optimization really works!",
      rating: 5,
      avatar: "https://images.pexels.com/photos/5921483/pexels-photo-5921483.jpeg"
    },
    {
      name: "Alex Kumar",
      role: "Marketing Professional", 
      company: "Job Seeker",
      content: "I've applied to over 50+ jobs in the last few months — startups, MNCs, remote roles, contract gigs — everything. I optimized my LinkedIn, rewrote my resume 10 times, even paid for templates and hired a 'professional resume writer' once. Still… nothing. A few ghosted interviews. Mostly rejections. Many no replies. I was ready to give up. I stumbled upon Shortlistly with a bit of skepticism. The landing page looked polished and promising, but so do a lot of tools out there.",
      rating: 5,
      avatar: "https://images.pexels.com/photos/5921483/pexels-photo-5921483.jpeg"
    },
    {
      name: "James Wilson",
      role: "Product Manager",
      company: "StartupCorp",
      content: "After months of rejections, Shortlistly's AI optimization got me shortlisted for 8 different companies in just 2 weeks. The results speak for themselves!",
      rating: 5,
      avatar: "https://images.pexels.com/photos/5921483/pexels-photo-5921483.jpeg"
    },
    {
      name: "Lisa Patel",
      role: "UX Designer",
      company: "Design Studio",
      content: "The personalized feedback and ATS optimization features are game-changing. I went from zero interviews to 5 job offers in one month!",
      rating: 5,
      avatar: "https://images.pexels.com/photos/5921483/pexels-photo-5921483.jpeg"
    }
  ];

  return <AutoRotatingTestimonials testimonials={testimonials} />;
};

// Before & After Section - Updated for Shortlistly
const BeforeAfterSection = () => {
  const [activeTab, setActiveTab] = useState('before');

  return (
    <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection className="text-center mb-16">
          <motion.h2 
            variants={fadeInUp}
            className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6"
          >
            See the Transformation
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="text-xl text-slate-600 max-w-3xl mx-auto"
          >
            Real resume transformation showing the power of Shortlistly's AI optimization
          </motion.p>
        </AnimatedSection>
        
        <AnimatedSection>
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            {/* Tab Navigation */}
            <div className="flex border-b border-slate-200">
              <button
                onClick={() => setActiveTab('before')}
                className={`flex-1 py-4 px-6 text-center font-semibold transition-colors ${
                  activeTab === 'before' 
                    ? 'bg-red-50 text-red-600 border-b-2 border-red-500' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Before Optimization
              </button>
              <button
                onClick={() => setActiveTab('after')}
                className={`flex-1 py-4 px-6 text-center font-semibold transition-colors ${
                  activeTab === 'after' 
                    ? 'bg-green-50 text-green-600 border-b-2 border-green-500' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                After Shortlistly
              </button>
            </div>
            
            {/* Content */}
            <div className="p-8">
              {activeTab === 'before' ? (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="grid lg:grid-cols-2 gap-8 items-center"
                >
                  <div>
                    <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
                      <h3 className="text-2xl font-bold text-slate-900 mb-4">Generic Resume</h3>
                      <ul className="space-y-2 text-slate-700">
                        <li className="flex items-center">
                          <div className="w-2 h-2 bg-red-400 rounded-full mr-3"></div>
                          No keyword optimization
                        </li>
                        <li className="flex items-center">
                          <div className="w-2 h-2 bg-red-400 rounded-full mr-3"></div>
                          Generic job descriptions
                        </li>
                        <li className="flex items-center">
                          <div className="w-2 h-2 bg-red-400 rounded-full mr-3"></div>
                          ATS rejection guaranteed
                        </li>
                        <li className="flex items-center">
                          <div className="w-2 h-2 bg-red-400 rounded-full mr-3"></div>
                          Never gets shortlisted
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div>
                    <img 
                      src="https://images.pexels.com/photos/9221770/pexels-photo-9221770.jpeg" 
                      alt="Before optimization"
                      className="w-full h-64 object-cover rounded-2xl"
                    />
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="grid lg:grid-cols-2 gap-8 items-center"
                >
                  <div>
                    <div className="bg-green-50 border border-green-200 rounded-2xl p-6">
                      <h3 className="text-2xl font-bold text-slate-900 mb-4">Shortlistly Optimized</h3>
                      <ul className="space-y-2 text-slate-700">
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />
                          Perfect keyword matching
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />
                          Tailored for success
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />
                          100% ATS compatible
                        </li>
                        <li className="flex items-center">
                          <Check className="w-5 h-5 text-green-500 mr-3" />
                          Gets you shortlisted
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div>
                    <img 
                      src="https://images.pexels.com/photos/7997398/pexels-photo-7997398.jpeg" 
                      alt="After Shortlistly optimization"
                      className="w-full h-64 object-cover rounded-2xl"
                    />
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// CTA Section - Updated for Shortlistly
// Pricing Section - Updated for Shortlistly
const PricingSection = () => {
  return (
    <section id="pricing" className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <motion.div 
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Choose Your Plan
            </h2>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Get shortlisted faster with our AI-powered resume optimization
            </p>
          </motion.div>
          
          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {/* Free Plan */}
            <motion.div 
              variants={fadeInUp}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
            >
              <div className="text-center">
                <h3 className="text-2xl font-bold text-white mb-2">Free</h3>
                <div className="text-4xl font-bold text-white mb-6">₹0</div>
                <ul className="space-y-3 text-blue-100 mb-8">
                  <li className="flex items-center">
                    <Check className="w-5 h-5 text-green-400 mr-3" />
                    Resume analysis
                  </li>
                  <li className="flex items-center">
                    <Check className="w-5 h-5 text-green-400 mr-3" />
                    ATS compatibility check
                  </li>
                  <li className="flex items-center">
                    <Check className="w-5 h-5 text-green-400 mr-3" />
                    Basic feedback
                  </li>
                </ul>
                <button 
                  onClick={() => {
                    const appSection = document.getElementById('app');
                    if (appSection) appSection.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="w-full py-3 bg-white/20 text-white border border-white/30 rounded-xl hover:bg-white/30 transition-all duration-300"
                >
                  Get Started Free
                </button>
              </div>
            </motion.div>

            {/* Basic Plan */}
            <motion.div 
              variants={fadeInUp}
              className="bg-white rounded-2xl p-8 shadow-2xl relative"
            >
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-orange-500 text-white px-6 py-2 rounded-full text-sm font-semibold">
                  Most Popular
                </span>
              </div>
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Basic</h3>
                <div className="text-4xl font-bold text-gray-900 mb-6">₹199</div>
                <ul className="space-y-3 text-gray-700 mb-8">
                  <li className="flex items-center">
                    <Check className="w-5 h-5 text-green-500 mr-3" />
                    3 resume optimizations
                  </li>
                  <li className="flex items-center">
                    <Check className="w-5 h-5 text-green-500 mr-3" />
                    3 professional templates
                  </li>
                  <li className="flex items-center">
                    <Check className="w-5 h-5 text-green-500 mr-3" />
                    PDF/DOCX downloads
                  </li>
                  <li className="flex items-center">
                    <Check className="w-5 h-5 text-green-500 mr-3" />
                    ATS optimization
                  </li>
                </ul>
                <button 
                  onClick={() => {
                    const appSection = document.getElementById('app');
                    if (appSection) appSection.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="w-full py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-300"
                >
                  Choose Basic
                </button>
              </div>
            </motion.div>

            {/* Premium Plan */}
            <motion.div 
              variants={fadeInUp}
              className="bg-gradient-to-br from-purple-600 to-indigo-600 rounded-2xl p-8 text-white shadow-2xl"
            >
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-2">Premium</h3>
                <div className="text-4xl font-bold mb-6">₹499</div>
                <ul className="space-y-3 text-purple-100 mb-8">
                  <li className="flex items-center">
                    <Check className="w-5 h-5 text-green-400 mr-3" />
                    Unlimited optimizations
                  </li>
                  <li className="flex items-center">
                    <Check className="w-5 h-5 text-green-400 mr-3" />
                    All 9 professional templates
                  </li>
                  <li className="flex items-center">
                    <Check className="w-5 h-5 text-green-400 mr-3" />
                    Photo integration
                  </li>
                  <li className="flex items-center">
                    <Check className="w-5 h-5 text-green-400 mr-3" />
                    Cover letter generation
                  </li>
                  <li className="flex items-center">
                    <Check className="w-5 h-5 text-green-400 mr-3" />
                    Priority support
                  </li>
                </ul>
                <button 
                  onClick={() => {
                    const appSection = document.getElementById('app');
                    if (appSection) appSection.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="w-full py-3 bg-white text-purple-600 rounded-xl hover:bg-gray-100 transition-all duration-300 font-semibold"
                >
                  Choose Premium
                </button>
              </div>
            </motion.div>
          </div>

          {/* CTA */}
          <motion.div 
            variants={fadeInUp}
            className="text-center"
          >
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 25px 50px -12px rgba(255, 255, 255, 0.3)" }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                const appSection = document.getElementById('app');
                if (appSection) appSection.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-8 py-4 bg-white text-blue-600 font-semibold rounded-2xl shadow-2xl hover:shadow-white/25 transition-all duration-300"
            >
              Get Shortlisted Free
            </motion.button>
            
            <motion.p 
              variants={fadeInUp}
              className="text-blue-200 mt-6"
            >
              No credit card required • Instant results • 98% success rate
            </motion.p>
          </motion.div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Footer - Updated for Shortlistly
const Footer = ({ onNavigate }) => {
  const handlePolicyClick = (path) => {
    window.location.href = path;
  };

  return (
    <footer className="bg-slate-900 text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8">
          {/* Logo and description */}
          <div className="md:col-span-2">
            <div className="flex items-center mb-4">
              <Award className="w-8 h-8 text-blue-400 mr-2" />
              <span className="text-2xl font-bold">Shortlistly</span>
            </div>
            <p className="text-slate-300 mb-6 max-w-md">
              Transform your career with AI-powered resume optimization. 
              Get shortlisted, get hired, get ahead.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-2">
              <h4 className="font-semibold text-blue-400 mb-3">📞 Contact Us</h4>
              <p className="text-slate-300">We're here to help you get shortlisted!</p>
              <p className="text-slate-300">
                Email: <a href="mailto:shortlistly@gmail.com" className="text-blue-400 hover:text-blue-300 transition-colors">shortlistly@gmail.com</a>
              </p>
              <button
                onClick={() => handlePolicyClick('/contact-us')}
                className="text-blue-400 hover:text-blue-300 transition-colors text-sm"
              >
                Contact Support →
              </button>
            </div>
          </div>
          
          {/* Product Links */}
          <div>
            <h3 className="font-semibold mb-4">Product</h3>
            <ul className="space-y-2 text-slate-300">
              <li><a href="#app" className="hover:text-white transition-colors">AI Resume Optimizer</a></li>
              <li><a href="#testimonials" className="hover:text-white transition-colors">Success Stories</a></li>
              <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
              <li><a href="#" className="hover:text-white transition-colors">Templates</a></li>
            </ul>
          </div>
          
          {/* Legal Links */}
          <div>
            <h3 className="font-semibold mb-4">Legal & Support</h3>
            <ul className="space-y-2 text-slate-300">
              <li>
                <button 
                  onClick={() => handlePolicyClick('/privacy-policy')}
                  className="hover:text-white transition-colors text-left"
                >
                  Privacy Policy
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handlePolicyClick('/terms-conditions')}
                  className="hover:text-white transition-colors text-left"
                >
                  Terms & Conditions
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handlePolicyClick('/refund-policy')}
                  className="hover:text-white transition-colors text-left"
                >
                  Refund Policy
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handlePolicyClick('/shipping-delivery')}
                  className="hover:text-white transition-colors text-left"
                >
                  Shipping & Delivery
                </button>
              </li>
              <li>
                <button
                  onClick={() => handlePolicyClick('/contact-us')}
                  className="hover:text-white transition-colors text-left"
                >
                  Contact Us
                </button>
              </li>
              <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
            </ul>
          </div>
        </div>

        {/* Copyright and Social */}
        <div className="border-t border-slate-700 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-slate-400 mb-4 md:mb-0">
              <p>© 2025 Shortlistly. All rights reserved.</p>
              <p className="text-sm mt-1">Get Shortlisted. Get Hired. Get Ahead.</p>
            </div>
            
            <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6">
              <div className="flex space-x-4 text-slate-400">
                <a href="#" className="hover:text-white transition-colors text-sm">Status</a>
                <a href="#" className="hover:text-white transition-colors text-sm">API</a>
                <a href="#" className="hover:text-white transition-colors text-sm">Sitemap</a>
              </div>
              
              <div className="flex items-center space-x-2 text-sm text-slate-400">
                <span>Made with</span>
                <span className="text-red-400">❤️</span>
                <span>for ambitious job seekers</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Shortlistly Resume App Section
const ShortlistlyResumeApp = () => {
  return (
    <section id="app" className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection className="text-center mb-12">
          <motion.h2 
            variants={fadeInUp}
            className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6"
          >
            Try Shortlistly Now
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="text-xl text-slate-600 max-w-3xl mx-auto mb-4"
          >
            See exactly why you're not getting shortlisted. Get instant ATS analysis and optimization recommendations - no signup required.
          </motion.p>
          <motion.div 
            variants={fadeInUp}
            className="inline-flex items-center px-4 py-2 bg-green-100 rounded-full border border-green-200"
          >
            <span className="text-green-800 font-semibold text-sm">
              ✅ Completely Free • ✅ No Email Required • ✅ Instant Results
            </span>
          </motion.div>
        </AnimatedSection>
        
        <EnhancedFreemiumResumeApp />
      </div>
    </section>
  );
};

// Main Shortlistly Landing Component
const ShortlistlyLanding = () => {
  const [currentView, setCurrentView] = useState('landing');

  const handleNavigation = (view) => {
    setCurrentView(view);
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'dashboard':
        return <DashboardReal onNavigate={handleNavigation} />;
      case 'privacy':
        return <PrivacyPolicy onBack={() => setCurrentView('landing')} />;
      case 'terms':
        return <TermsConditions onBack={() => setCurrentView('landing')} />;
      case 'refund':
        return <RefundPolicy onBack={() => setCurrentView('landing')} />;
      case 'shipping':
        return <ShippingPolicy onBack={() => setCurrentView('landing')} />;
      case 'contact':
        return <ContactUs onBack={() => setCurrentView('landing')} />;
      default:
        return (
          <div className="min-h-screen bg-white">
            <Header onNavigate={handleNavigation} currentView={currentView} />
            <HeroSection />
            <ShortlistlyResumeApp />
            <HighlightedQuoteSection />
            <HowItWorksSection />
            <EnhancedTestimonialsSection />
            <BeforeAfterSection />
            <PricingSection />
            <Footer onNavigate={handleNavigation} />
          </div>
        );
    }
  };

  return renderCurrentView();
};

export default ShortlistlyLanding;
